'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, ShoppingCart, Package, Receipt, TrendingUp, ChevronLeft, ChevronRight, ShoppingBag, FlaskConical, Users, LogOut, DollarSign, BarChart2, Sparkles, Box, ClipboardList, UserCheck } from 'lucide-react';
import { store } from '@/lib/store';
import type { UserProfile } from '@/lib/store';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
  group?: string;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', href: '/', icon: <LayoutDashboard size={18} />, group: 'Principal' },
  { id: 'nav-ventas', label: 'Ventas / POS', href: '/ventas-punto-de-venta', icon: <ShoppingCart size={18} />, group: 'Operaciones' },
  { id: 'nav-productos', label: 'Productos', href: '/productos', icon: <Package size={18} />, group: 'Operaciones' },
  { id: 'nav-extras', label: 'Extras', href: '/extras', icon: <Sparkles size={18} />, group: 'Operaciones' },
  { id: 'nav-empaques', label: 'Empaques', href: '/empaques', icon: <Box size={18} />, group: 'Operaciones' },
  { id: 'nav-ingredientes', label: 'Ingredientes', href: '/ingredientes', icon: <FlaskConical size={18} />, group: 'Operaciones' },
  { id: 'nav-compras', label: 'Compras', href: '/compras', icon: <ShoppingBag size={18} />, group: 'Operaciones' },
  { id: 'nav-gastos', label: 'Gastos Operativos', href: '/gastos', icon: <DollarSign size={18} />, group: 'Operaciones' },
  { id: 'nav-empleados', label: 'Empleados / Nómina', href: '/empleados', icon: <UserCheck size={18} />, group: 'Operaciones' },
  { id: 'nav-kardex', label: 'Kardex Inventario', href: '/kardex', icon: <ClipboardList size={18} />, group: 'Operaciones' },
  { id: 'nav-recibos', label: 'Recibos', href: '/recibos', icon: <Receipt size={18} />, group: 'Reportes' },
  { id: 'nav-finanzas', label: 'Finanzas', href: '/finanzas', icon: <TrendingUp size={18} />, group: 'Reportes' },
  { id: 'nav-reportes', label: 'Reportes PDF', href: '/reportes', icon: <BarChart2 size={18} />, group: 'Reportes' },
  { id: 'nav-perfiles', label: 'Perfiles', href: '/perfiles', icon: <Users size={18} />, group: 'Sistema' },
];

const groups = ['Principal', 'Operaciones', 'Reportes', 'Sistema'];

export default function Sidebar() {
  const pathname = usePathname();
  const router = useRouter();
  const [collapsed, setCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    setCurrentUser(store.getCurrentUser());
  }, []);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const handleLogout = () => {
    store.logout();
    router.replace('/login');
  };

  return (
    <aside
      className="relative flex flex-col bg-card border-r border-border h-screen sticky top-0 transition-all duration-300 ease-in-out flex-shrink-0"
      style={{ width: collapsed ? 72 : 240 }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-border overflow-hidden">
        <AppLogo size={36} />
        {!collapsed && (
          <span className="font-bold text-lg text-foreground tracking-tight whitespace-nowrap">
            Bunori POS
          </span>
        )}
      </div>

      {/* Toggle button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-16 z-10 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-primary transition-all duration-150 hover:scale-110"
        aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-3">
        {groups.map((group) => {
          const groupItems = navItems.filter((n) => n.group === group);
          if (groupItems.length === 0) return null;
          return (
            <div key={`group-${group}`} className="mb-4">
              {!collapsed && (
                <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground px-3 mb-1">
                  {group}
                </p>
              )}
              {groupItems.map((item) => (
                <Link
                  key={item.id}
                  href={item.href}
                  className={`sidebar-link mb-0.5 relative group ${isActive(item.href) ? 'active' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!collapsed && (
                    <span className="flex-1 whitespace-nowrap">{item.label}</span>
                  )}
                  {!collapsed && item.badge && (
                    <span className="ml-auto flex-shrink-0 w-5 h-5 rounded-full bg-accent text-accent-foreground text-[10px] font-700 flex items-center justify-center">
                      {item.badge}
                    </span>
                  )}
                  {collapsed && item.badge && (
                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent" />
                  )}
                  {collapsed && (
                    <span className="pointer-events-none absolute left-full ml-2 px-2 py-1 bg-foreground text-primary-foreground text-xs font-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap z-50">
                      {item.label}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          );
        })}
      </nav>

      {/* Bottom: user + logout */}
      <div className="border-t border-border px-3 py-4 space-y-1">
        <button
          onClick={handleLogout}
          className="sidebar-link w-full text-[var(--status-red)] hover:bg-[var(--status-red-bg)]"
          title={collapsed ? 'Cerrar sesión' : undefined}
        >
          <LogOut size={18} />
          {!collapsed && <span>Cerrar sesión</span>}
          {collapsed && (
            <span className="pointer-events-none absolute left-full ml-2 px-2 py-1 bg-foreground text-primary-foreground text-xs font-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap z-50">
              Cerrar sesión
            </span>
          )}
        </button>
        <div className={`flex items-center gap-3 px-3 py-2 rounded-xl mt-1 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-sm font-700 flex-shrink-0">
            {currentUser?.name?.charAt(0)?.toUpperCase() || 'B'}
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <p className="text-sm font-600 text-foreground truncate">{currentUser?.name || 'Bunori POS'}</p>
              <p className="text-xs text-muted-foreground truncate">{currentUser?.role || 'Admin'}</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}